import React, { useState } from "react";
import { Eye, EyeOff, KeyRound, ShieldCheck, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { isAuthError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";

export const PASSWORD_MIN_LENGTH = 8;

const ERROR_COPY = {
  CURRENT_PASSWORD_INCORRECT: "Password saat ini tidak sesuai.",
  PASSWORD_CONFIRMATION_MISMATCH: "Konfirmasi password baru tidak sama.",
  PASSWORD_SAME_AS_CURRENT: "Password baru harus berbeda dari password saat ini.",
  RATE_LIMITED: "Terlalu banyak percobaan. Coba lagi beberapa saat.",
  UNKNOWN: "Password gagal diubah. Silakan coba lagi.",
};

// Error API → pesan aman (kode backend; PASSWORD_POLICY_FAILED memakai pesan policy backend).
export function describeChangePasswordError(err) {
  const status = err?.response?.status;
  const d = err?.response?.data?.detail;
  if (status === 429) return ERROR_COPY.RATE_LIMITED;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    if (d.code === "PASSWORD_POLICY_FAILED" && typeof d.message === "string") return d.message;
    if (ERROR_COPY[d.code]) return ERROR_COPY[d.code];
  }
  return ERROR_COPY.UNKNOWN;
}

// Validasi sisi klien (server tetap memvalidasi ulang).
export function validateChangePasswordForm({ current_password, new_password, confirm_password }) {
  if (!current_password) return "Password saat ini wajib diisi.";
  if (!new_password || !new_password.trim()) return "Password baru tidak boleh kosong.";
  if (new_password.length < PASSWORD_MIN_LENGTH) return `Password baru minimal ${PASSWORD_MIN_LENGTH} karakter.`;
  if (new_password === current_password) return ERROR_COPY.PASSWORD_SAME_AS_CURRENT;
  if (new_password !== confirm_password) return ERROR_COPY.PASSWORD_CONFIRMATION_MISMATCH;
  return null;
}

const EMPTY = { current_password: "", new_password: "", confirm_password: "" };

function PasswordField({ id, label, value, onChange, autoComplete, disabled }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          disabled={disabled}
          className="pr-10"
          data-testid={`${id}-input`}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#7A5440] hover:text-[#4A2E1F] focus:outline-none"
          aria-label={show ? `Sembunyikan ${label}` : `Tampilkan ${label}`}
          data-testid={`${id}-toggle`}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}

export function ChangePasswordForm() {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (v) => { setForm((f) => ({ ...f, [k]: v })); setError(null); setSuccess(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const clientError = validateChangePasswordForm(form);
    if (clientError) { setError(clientError); return; }
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/admin/account/change-password", form);
      setForm(EMPTY);
      setSuccess(true);
      toast.success("Password berhasil diubah.");
    } catch (err) {
      if (isAuthError(err)) return;
      setError(describeChangePasswordError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="admin-change-password-form" noValidate>
      <PasswordField id="current-password" label="Password Saat Ini" value={form.current_password}
        onChange={set("current_password")} autoComplete="current-password" disabled={submitting} />
      <PasswordField id="new-password" label="Password Baru" value={form.new_password}
        onChange={set("new_password")} autoComplete="new-password" disabled={submitting} />
      <PasswordField id="confirm-password" label="Konfirmasi Password Baru" value={form.confirm_password}
        onChange={set("confirm_password")} autoComplete="new-password" disabled={submitting} />
      <p className="text-xs text-[#7A5440]">Minimal {PASSWORD_MIN_LENGTH} karakter dan berbeda dari password saat ini.</p>
      {error && (
        <div role="alert" className="text-sm px-3 py-2 rounded-lg bg-red-100 text-red-800 border border-red-200"
          data-testid="change-password-error">{error}</div>
      )}
      {success && (
        <div role="status" className="text-sm px-3 py-2 rounded-lg bg-emerald-100 text-emerald-800 border border-emerald-200"
          data-testid="change-password-success">Password berhasil diubah.</div>
      )}
      <Button type="submit" disabled={submitting} className="bg-[#4A2E1F] text-white" data-testid="change-password-submit">
        <KeyRound size={16} className="mr-2" /> {submitting ? "Menyimpan..." : "Ubah Password"}
      </Button>
    </form>
  );
}

export default function AccountSecurity() {
  const { user } = useAuth();
  return (
    <AdminLayout title="Profil & Keamanan">
      <div className="max-w-3xl space-y-6" data-testid="admin-account-security">
        <Card className="p-6 flex items-center gap-4">
          <UserCircle2 className="text-[#4A2E1F] shrink-0" size={40} />
          <div className="min-w-0">
            <div className="text-xs text-[#5C5C5C]">Login sebagai</div>
            <div className="font-semibold truncate" data-testid="account-email">{user?.email}</div>
            {user?.name && <div className="text-sm text-[#5C5C5C] truncate">{user.name}</div>}
          </div>
        </Card>
        <Card className="p-6 space-y-5">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-[#4A2E1F]" size={24} />
            <div>
              <h2 className="text-lg font-semibold">Keamanan Akun</h2>
              <p className="text-sm text-[#5C5C5C]">Ubah Password</p>
            </div>
          </div>
          <ChangePasswordForm />
        </Card>
      </div>
    </AdminLayout>
  );
}
